import { useNavigate } from 'react-router-dom';
import { Plus, Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PriorityBadge } from '@/components/shared/PriorityBadge';
import { UserAvatar } from '@/components/shared/UserAvatar';
import type { SprintDetail } from '@/services/sprint.service';
import type { Task } from '@/services/task.service';

interface SprintKanbanViewProps {
  sprint: SprintDetail;
  tasks: Task[];
  canManageColumn: (columnId: string) => boolean;
  isLocked: boolean;
  onCreateTask: (columnId: string, columnName: string) => void;
  onViewTask?: (taskId: string) => void;
}

export function SprintKanbanView({
  sprint, tasks, canManageColumn, isLocked, onCreateTask, onViewTask,
}: SprintKanbanViewProps) {
  const navigate = useNavigate();

  // Group tasks by column — tasks without column_id go into the first column
  const tasksByColumn: Record<string, Task[]> = {};
  for (const col of sprint.columns) tasksByColumn[col.id] = [];
  const firstColumnId = sprint.columns[0]?.id;
  for (const task of tasks) {
    if (task.column_id && tasksByColumn[task.column_id]) {
      tasksByColumn[task.column_id].push(task);
    } else if (firstColumnId && tasksByColumn[firstColumnId]) {
      tasksByColumn[firstColumnId].push(task);
    }
  }
  // Sort by display_order
  for (const colId of Object.keys(tasksByColumn)) {
    tasksByColumn[colId].sort((a, b) => a.display_order - b.display_order);
  }

  const sortedColumns = [...sprint.columns].sort((a, b) => a.display_order - b.display_order);

  return (
    <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 400 }}>
      {sortedColumns.map((col) => {
        const colTasks = tasksByColumn[col.id] || [];
        const leaders = col.leaders || [];
        const totalEst = colTasks.reduce((s, t) => s + (t.estimated_hours || 0), 0);
        const totalAct = colTasks.reduce((s, t) => s + (t.actual_hours || 0), 0);
        const canEdit = canManageColumn(col.id);

        return (
          <div key={col.id} className="flex w-72 shrink-0 flex-col rounded-lg bg-muted/50 border">
            {/* Column header */}
            <div className="px-3 py-2.5 border-b space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">{col.name}</h3>
                  <Badge variant="secondary" className="text-[10px]">
                    {colTasks.length}
                  </Badge>
                </div>
                {canEdit && !isLocked && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => onCreateTask(col.id, col.name)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <span>{totalEst.toFixed(1)}h est / {totalAct.toFixed(1)}h actual</span>
                <div className="flex -space-x-1">
                  {leaders.map((l: any) => (
                    <UserAvatar
                      key={l.id}
                      name={l.user?.full_name || '?'}
                      avatarUrl={l.user?.avatar_url}
                      size="xs"
                      className="ring-2 ring-background"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Task cards */}
            <ScrollArea className="flex-1 min-h-0">
              <div className="flex flex-col gap-2 p-2 min-h-[80px]">
                {colTasks.map((task) => (
                  <KanbanTaskCard key={task.id} task={task} onClick={() => onViewTask ? onViewTask(task.id) : navigate(`/tasks/${task.id}`)} />
                ))}
                {colTasks.length === 0 && (
                  <div className="py-6 text-center">
                    <p className="text-xs text-muted-foreground">No tasks</p>
                    {canEdit && !isLocked && (
                      <button
                        type="button"
                        onClick={() => onCreateTask(col.id, col.name)}
                        className="text-xs text-brand-primary hover:underline mt-1"
                      >
                        Add a task
                      </button>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        );
      })}
    </div>
  );
}

function KanbanTaskCard({ task, onClick }: { task: Task; onClick: () => void }) {
  return (
    <Card
      className="p-3 cursor-pointer hover:shadow-md transition-shadow border-l-[3px] space-y-2"
      style={{ borderLeftColor: getPriorityColor(task.priority) }}
      onClick={onClick}
    >
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-[10px] text-muted-foreground">{task.reference_id}</span>
      </div>
      <p className="text-sm font-medium line-clamp-2">{task.title}</p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <PriorityBadge priority={task.priority} />
        <StatusBadge status={task.phase_status} />
      </div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          <span>{(task.estimated_hours || 0).toFixed(1)}h / {(task.actual_hours || 0).toFixed(1)}h</span>
        </div>
        {task.end_date && (
          <span>{new Date(task.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        )}
      </div>
      <div className="flex items-center justify-between">
        {task.assignee ? (
          <UserAvatar name={task.assignee.full_name} avatarUrl={task.assignee.avatar_url} size="xs" />
        ) : (
          <span className="text-[10px] text-muted-foreground">Unassigned</span>
        )}
      </div>
    </Card>
  );
}

function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'critical': return '#DC2626';
    case 'high': return '#EA580C';
    case 'medium': return '#D97706';
    case 'low': return '#2563EB';
    default: return '#9CA3AF';
  }
}
